import React, { useState } from "react";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import InputGroup from "react-bootstrap/InputGroup";

import { apiPost } from "../../api/server"
import MediaLibrary from "./MediaLibrary"

/*
 * Settings for a speed networking event (eventtype 7), saved to events.config
 * via intranetevents/updatespeednetworking. The scheduler on BB-Server
 * (functions/speednetworking) reads them once, when the lobby opens 10
 * minutes before the start, so changes after that don't affect the event.
 *
 * Videos are links to a video file (e.g. an .mp4 uploaded to S3), not a
 * YouTube page: they're played in step for everyone, which needs a plain
 * video file. Their length is read from the file itself. They can be pasted
 * in, or uploaded to and picked from the media library.
 */

const DASHBOARD_URL = process.env.REACT_APP_DASHBOARD_URL || "https://member-dashboard.findabusinessexpert.com"

// Mirrors BB-Server: the lobby opens LOBBY_OPEN_MINUTES before the start, and
// round 1 begins LOBBY_LOCK_BUFFER_MS after the rounds' start.
const LOBBY_OPEN_MINUTES = 10
const ROUND_ONE_BUFFER_SECONDS = 30

const DEFAULTS = { roundCount: 5, roundSeconds: 420, breakSeconds: 30, introVideoUrl: "", introSeconds: 0, introDelaySeconds: 120, breakVideoUrls: [] }

function formatTime(ms){
  return new Date(ms).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Europe/London" })
}

function formatLength(seconds){
  const s = Math.round(seconds)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
}

// Loads just the video's metadata to read its length, which also checks the
// link works before it's needed on the day. Keyed by url where it's used, so
// a new link starts checking afresh.
const VideoLength = ({ url, onLength }) => {
  const [status, setStatus] = useState("loading")
  if(!url) return null
  return (
    <>
      <video
        src={url}
        preload="metadata"
        style={{display: "none"}}
        onLoadedMetadata={(e) => { setStatus("ok"); onLength(e.target.duration) }}
        onError={() => setStatus("error")}
      />
      {status === "loading" && <Form.Text muted>Checking the video…</Form.Text>}
      {status === "error" && <Form.Text style={{color: "red"}}>This video couldn't be loaded. Check it's a direct link to a video file.</Form.Text>}
    </>
  )
}

const SpeedNetworkingSettings = ({ event, starttime, endtime }) => {
  const saved = { ...DEFAULTS, ...(event.config || {}) }

  const [roundCount, setRoundCount] = useState(saved.roundCount)
  const [roundMinutes, setRoundMinutes] = useState(saved.roundSeconds / 60)
  const [breakSeconds, setBreakSeconds] = useState(saved.breakSeconds)
  const [introVideoUrl, setIntroVideoUrl] = useState(saved.introVideoUrl || "")
  const [introSeconds, setIntroSeconds] = useState(saved.introSeconds)
  const [introDelayMinutes, setIntroDelayMinutes] = useState(saved.introDelaySeconds / 60)
  const [breakVideoUrls, setBreakVideoUrls] = useState((saved.breakVideoUrls || []).map(url => url || ""))
  const [breakLengths, setBreakLengths] = useState({})

  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null) // { variant, text }
  const [copied, setCopied] = useState(false)
  // Which video the media library is choosing for: "intro", a break's index,
  // or "browse" for the whole library with nothing to choose.
  const [library, setLibrary] = useState(null)

  const breakCount = Math.max(0, Number(roundCount) - 1)
  const roundSeconds = Math.round(Number(roundMinutes) * 60)
  const hasIntro = !!introVideoUrl.trim() && Number(introSeconds) > 0
  const guestLink = `${DASHBOARD_URL}/speed-networking/${event.id}/guest`

  // The same timeline BB-Server builds (eventStart.js), for checking at a glance.
  const startMs = starttime.getTime()
  const introStartMs = startMs + Number(introDelayMinutes) * 60000
  const roundsStartMs = hasIntro ? introStartMs + Number(introSeconds) * 1000 : startMs
  const roundOneMs = roundsStartMs + ROUND_ONE_BUFFER_SECONDS * 1000
  const lastRoundEndMs = roundOneMs + Number(roundCount) * roundSeconds * 1000 + breakCount * Number(breakSeconds) * 1000

  function setBreakVideo(index, value){
    setBreakVideoUrls(prev => {
      const next = [...prev]
      while(next.length <= index) next.push("")
      next[index] = value
      return next
    })
  }

  function copyFirstToAll(){
    const first = breakVideoUrls[0] || ""
    setBreakVideoUrls(Array.from({ length: breakCount }, () => first))
  }

  function chooseVideo(url){
    if(library === "intro") setIntroVideoUrl(url)
    else setBreakVideo(library, url)
    setLibrary(null)
  }

  function copyGuestLink(){
    navigator.clipboard.writeText(guestLink).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function save(){
    setSaving(true)
    setMessage(null)
    apiPost(`intranetevents/updatespeednetworking`, {
      id: event.id,
      roundCount: Number(roundCount),
      roundSeconds: roundSeconds,
      breakSeconds: Number(breakSeconds),
      introVideoUrl: introVideoUrl.trim(),
      introSeconds: introVideoUrl.trim() ? Math.ceil(Number(introSeconds)) : 0,
      introDelaySeconds: Math.round(Number(introDelayMinutes) * 60),
      breakVideoUrls: breakVideoUrls.slice(0, breakCount).map(url => url.trim())
    })
      .then(res => res.json())
      .then(data => {
        setSaving(false)
        if(data.status !== "ok"){
          setMessage({ variant: "danger", text: data.error || "Couldn't save the settings" })
        }else if(data.started){
          setMessage({ variant: "warning", text: "Saved, but this event has already started, so the changes won't apply to it." })
        }else{
          setMessage({ variant: "success", text: "Speed networking settings saved." })
        }
      })
      .catch(err => { console.log(err); setSaving(false); setMessage({ variant: "danger", text: "Something went wrong" }) })
  }

  // This sits inside the event page's <Form>, where Enter in a field would
  // submit that form (Save and Exit) rather than these settings.
  function blockEnter(e){
    if(e.key === "Enter" && e.target.tagName === "INPUT") e.preventDefault()
  }

  return (
    <div className="section" onKeyDown={blockEnter}>
      <h2>Speed Networking</h2>

      <Form.Group controlId="speedNetworking.guestLink">
        <Form.Label>Guest link (for non-members, e.g. in the Eventbrite confirmation)</Form.Label>
        <InputGroup>
          <Form.Control readOnly value={guestLink} />
          <InputGroup.Append>
            <Button variant="outline-secondary" onClick={copyGuestLink}>{copied ? "Copied" : "Copy"}</Button>
          </InputGroup.Append>
        </InputGroup>
      </Form.Group>

      <h4>Rounds</h4>
      <Row>
        <Col md={4}>
          <Form.Group controlId="speedNetworking.roundCount">
            <Form.Label>Number of rounds</Form.Label>
            <Form.Control type="number" min={1} max={20} value={roundCount} onChange={(e) => setRoundCount(e.target.value)} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="speedNetworking.roundMinutes">
            <Form.Label>Round length (minutes)</Form.Label>
            <Form.Control type="number" min={1} max={60} step={0.5} value={roundMinutes} onChange={(e) => setRoundMinutes(e.target.value)} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="speedNetworking.breakSeconds">
            <Form.Label>Break length (seconds)</Form.Label>
            <Form.Control type="number" min={0} max={600} value={breakSeconds} onChange={(e) => setBreakSeconds(e.target.value)} />
          </Form.Group>
        </Col>
      </Row>

      <h4>Intro video</h4>
      <p>Leave blank when the host is there live: round 1 then starts at the start time. With a video, it plays a few minutes after the start and round 1 begins when it ends.</p>
      <Row>
        <Col md={6}>
          <Form.Group controlId="speedNetworking.introVideoUrl">
            <Form.Label>Video link</Form.Label>
            <InputGroup>
              <Form.Control type="text" placeholder="https://…/intro.mp4" value={introVideoUrl} onChange={(e) => setIntroVideoUrl(e.target.value)} />
              <InputGroup.Append>
                <Button variant="outline-secondary" onClick={() => setLibrary("intro")}>Choose…</Button>
              </InputGroup.Append>
            </InputGroup>
            <VideoLength key={introVideoUrl.trim()} url={introVideoUrl.trim()} onLength={(seconds) => setIntroSeconds(Math.ceil(seconds))} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="speedNetworking.introDelayMinutes">
            <Form.Label>Starts after (minutes)</Form.Label>
            <Form.Control type="number" min={0} max={30} step={0.5} value={introDelayMinutes} onChange={(e) => setIntroDelayMinutes(e.target.value)} disabled={!introVideoUrl.trim()} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="speedNetworking.introSeconds">
            <Form.Label>Length (seconds)</Form.Label>
            <Form.Control type="number" min={0} max={1800} value={introSeconds} onChange={(e) => setIntroSeconds(e.target.value)} disabled={!introVideoUrl.trim()} />
            {introVideoUrl.trim() && <Form.Text muted>Filled in from the video.</Form.Text>}
          </Form.Group>
        </Col>
      </Row>

      <h4>Break videos</h4>
      {breakCount === 0 ? <p>There are no breaks with one round.</p> : <>
        <p>Optional, one per break. A video longer than the break ({breakSeconds}s) is cut off when the next round starts.</p>
        {Array.from({ length: breakCount }, (_, index) => {
          const url = (breakVideoUrls[index] || "").trim()
          const length = breakLengths[url]
          return (
            <Form.Group key={index} controlId={`speedNetworking.breakVideo${index}`}>
              <Form.Label>Break {index + 1} (after round {index + 1})</Form.Label>
              <InputGroup>
                <Form.Control type="text" placeholder="https://…/break.mp4" value={breakVideoUrls[index] || ""} onChange={(e) => setBreakVideo(index, e.target.value)} />
                <InputGroup.Append>
                  <Button variant="outline-secondary" onClick={() => setLibrary(index)}>Choose…</Button>
                </InputGroup.Append>
              </InputGroup>
              <VideoLength key={url} url={url} onLength={(seconds) => setBreakLengths(prev => ({ ...prev, [url]: seconds }))} />
              {url && length > Number(breakSeconds) && (
                <Form.Text muted>Runs {formatLength(length)}; it'll be cut off after {formatLength(Number(breakSeconds))}.</Form.Text>
              )}
            </Form.Group>
          )
        })}
        {breakCount > 1 && (breakVideoUrls[0] || "").trim() && (
          <Button variant="outline-secondary" size="sm" onClick={copyFirstToAll} style={{marginBottom: "15px"}}>Use break 1's video for every break</Button>
        )}
      </>}

      <h4>Media library</h4>
      <p>Upload videos, images and music for speed networking, or copy a file's link. "Choose…" next to a video picks from the same library.</p>
      <Button variant="outline-secondary" onClick={() => setLibrary("browse")} style={{marginBottom: "15px"}}>Open media library</Button>
      <MediaLibrary
        show={library !== null}
        onHide={() => setLibrary(null)}
        kinds={library === "browse" ? undefined : ["videos"]}
        title={library === "intro" ? "Choose the intro video" : typeof library === "number" ? `Choose break ${library + 1}'s video` : undefined}
        onSelect={library === "browse" ? undefined : chooseVideo}
        selectedUrl={library === "intro" ? introVideoUrl.trim() : typeof library === "number" ? (breakVideoUrls[library] || "").trim() : undefined}
      />

      <h4>Timeline</h4>
      <ul>
        <li>Lobby opens: {formatTime(startMs - LOBBY_OPEN_MINUTES * 60000)}</li>
        <li>Start: {formatTime(startMs)}</li>
        {hasIntro && <li>Intro video: {formatTime(introStartMs)} to {formatTime(roundsStartMs)}</li>}
        <li>Round 1 starts: {formatTime(roundOneMs)}</li>
        <li>Last round ends: {formatTime(lastRoundEndMs)}</li>
      </ul>
      {endtime.getTime() < lastRoundEndMs && (
        <Alert variant="warning" transition={false}>
          The event's end time ({formatTime(endtime.getTime())}) is before the last round ends. Set it to {formatTime(lastRoundEndMs)} or later above, then Save.
        </Alert>
      )}

      {/* No fade on these Alerts: the Auth0 styleguide CSS hides Bootstrap 4's .fade.show. */}
      {message && <Alert variant={message.variant} transition={false}>{message.text}</Alert>}
      <Button onClick={save} disabled={saving} style={{marginBottom: "30px"}}>
        {saving ? "Saving…" : "Save speed networking settings"}
      </Button>
    </div>
  )
}

export default SpeedNetworkingSettings
